import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';

import { BasicModal, PageTabs, TranslatedNavLink } from 'common/components';
import { withTheme } from 'common/styling/theme';

import { TwoStepEmailForm, TwoStepGoogleForm } from 'auth/twoStepAuth/_components';
import { getJWTToken, resetAuth, resendOTPToken } from '../_redux';
import { LoginForm, AutologinEmailConfirmError } from '../_components';

import { getDynamicStyles } from './style';
import { increaseInvitationCodeClicks } from '../api';

const LoginPage = ({
  authError,
  authStep,
  resetAuth,
  isAutologinEmailConfirmError,
  location,
  history,
  theme,
  telegramAuthError,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const query = queryString.parse(location.search);

  const { telegramAuth } = queryString.parse(location.search);
  const isTelegramAuth = telegramAuth === 'true' || telegramAuthError;
  const [rememberUser, setRememberUser] = useState(false);

  // 从URL和localStorage获取salesCode
  const querySalesCode = queryString.parse(location.search).sales_code;
  const salesCode = querySalesCode || localStorage.getItem('salesCode');
  const queryShare = queryString.parse(location.search).share;
  const share = queryShare || localStorage.getItem('share');
  const authSearch = queryString.stringify({
    ...(salesCode ? { sales_code: salesCode } : {}),
    ...(share ? { share } : {}),
    ...(isTelegramAuth ? { telegramAuth: true } : {}),
  });
  const loginUrl = `/auth/login${authSearch ? `?${authSearch}` : ''}`;
  const registrationUrl = `/auth/register${authSearch ? `?${authSearch}` : ''}`;

  // 调用IncreaseInvitationCodeClicksView接口增加邀请码点击次数
  useEffect(() => {
    if (salesCode) {
      const increaseClicks = async () => {
        try {
          await increaseInvitationCodeClicks(salesCode);
        } catch (error) {
          // 忽略错误，不影响登录流程
          console.error('Failed to increase invitation code clicks:', error);
        }
      };

      increaseClicks();
    }
  }, [salesCode]);

  useEffect(() => {
    if (telegramAuthError)
      history.replace({
        ...location,
        search: queryString.stringify({
          ...query,
          telegramAuth: true,
        }),
      });
  }, [telegramAuthError]);

  return (
    <div className="LoginPage">
      {isAutologinEmailConfirmError ? (
        <AutologinEmailConfirmError resetAuth={resetAuth} />
      ) : (
        <>
          <PageTabs forAuth>
            <TranslatedNavLink navLinkId="login-page-nav-to-login-page-active" id="authLogin" to={loginUrl} />
            <TranslatedNavLink navLinkId="login-page-nav-to-registration-page" id="authRegister" to={registrationUrl} />
          </PageTabs>
          {query.message && (
            <FormattedMessage id={query.message}>
              {txt => (
                <div className="Auth__message">
                  <p>{txt}</p>
                </div>
              )}
            </FormattedMessage>
          )}
          {isTelegramAuth && !authError && (
            <div className="Auth__message">
              <FormattedMessage id="telegramAuthMessage">{txt => <p>{txt}</p>}</FormattedMessage>

              <FormattedMessage id="telegramAuthHint">
                {txt => <p className="Auth__message_hint">{txt}</p>}
              </FormattedMessage>
            </div>
          )}
          {/* 将salesCode传递给LoginForm */}
          <LoginForm
            authError={authError}
            isTelegramAuth={isTelegramAuth}
            setRememberUser={setRememberUser}
            salesCode={salesCode || ''}
            share={share || ''}
          />
          <BasicModal isOpen={!!authStep} captionId="authTwoStepAuthentication" onRequestClose={() => resetAuth('')}>
            {authStep === 'email_login' && (
              <TwoStepEmailForm
                isVerify
                rememberUser={rememberUser}
                onResend={() => resendOTPToken.request()}
                onSubmit={payload => getJWTToken({ ...payload, share: share || undefined })}
                onClose={() => resetAuth('')}
              />
            )}
            {authStep === 'totp_login' && (
              <TwoStepGoogleForm
                isLogin
                rememberUser={rememberUser}
                onSubmit={payload => getJWTToken({ ...payload, share: share || undefined })}
                onClose={() => resetAuth('')}
              />
            )}
          </BasicModal>
        </>
      )}
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

// 更新propTypes
LoginPage.propTypes = {
  resetAuth: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  authStep: PropTypes.string,
  authError: PropTypes.string,
  isAutologinEmailConfirmError: PropTypes.bool.isRequired,
  telegramAuthError: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

LoginPage.defaultProps = {
  authStep: '',
  authError: '',
  theme: {},
};

const ThemedLoginPage = withTheme()(
  connect(
    state => ({
      authStep: state.auth.login.authStep,
      authError: state.auth.login.authError,
      isAutologinEmailConfirmError: state.auth.login.isAutologinEmailConfirmError,
      telegramAuthError: state.auth.login.telegramAuthError,
    }),
    { resetAuth }
  )(LoginPage)
);

export default ThemedLoginPage;
export { LoginPage };
